import { useEffect, useState, type FormEvent } from "react";
import { useProfiles } from "../profiles/ProfileContext";
import { PageHeader, Panel } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import { CRM_TYPES, CRM_STATUSES } from "../lib/crm";
import { HIRING_TYPES } from "../lib/jobs";
import {
  listCampaigns, createCampaign, sendCampaign, getCampaign, previewAudience,
  CAMPAIGN_CHANNELS, type Campaign, type CampaignStats,
} from "../lib/marketing";

const BLANK = { name: "", channel: "email", subject: "", body: "", audience_type: "", audience_status: "" };

export function Marketing() {
  const { active } = useProfiles();
  const isHiring = active ? HIRING_TYPES.includes(active.type) : false;
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [audience, setAudience] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ campaign: Campaign; stats: CampaignStats } | null>(null);

  async function refresh() {
    setCampaigns((await listCampaigns()).campaigns);
  }
  useEffect(() => {
    if (!isHiring) { setLoading(false); return; }
    refresh().finally(() => setLoading(false));
  }, [isHiring]);

  // Live audience size as the segment changes (retried once so a cold-start
  // transient doesn't leave the count stuck).
  useEffect(() => {
    if (!showForm) return;
    let alive = true;
    setAudience(null);
    const load = (retries: number): Promise<void> =>
      previewAudience(form.audience_type || undefined, form.audience_status || undefined)
        .then((r) => { if (alive) setAudience(r.count); })
        .catch(() => { if (alive && retries > 0) return new Promise<void>((res) => setTimeout(() => res(load(retries - 1)), 800)); });
    void load(2);
    return () => { alive = false; };
  }, [showForm, form.audience_type, form.audience_status]);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy("new");
    try {
      await createCampaign({
        name: form.name, channel: form.channel, subject: form.subject, body: form.body,
        audience_type: form.audience_type || undefined, audience_status: form.audience_status || undefined,
      });
      setForm({ ...BLANK });
      setShowForm(false);
      await refresh();
    } finally { setBusy(null); }
  }

  async function send(c: Campaign) {
    setBusy(c.id);
    try { await sendCampaign(c.id); await refresh(); if (open === c.id) setDetail(await getCampaign(c.id)); }
    finally { setBusy(null); }
  }

  async function openCampaign(id: string) {
    if (open === id) { setOpen(null); setDetail(null); return; }
    setOpen(id);
    setDetail(await getCampaign(id));
  }

  const segmentLabel = (c: Campaign) =>
    [c.audience_type ? labelize(c.audience_type) : "All contacts", c.audience_status ? labelize(c.audience_status) : ""].filter(Boolean).join(" · ");

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Marketing"
        subtitle="Run email and SMS campaigns to your CRM contacts."
        actions={isHiring ? <Button onClick={() => setShowForm((v) => !v)}>{showForm ? "Cancel" : "New campaign"}</Button> : undefined}
      />

      {!isHiring ? (
        <p className="text-sm text-muted">Switch to a company or recruiter profile to run campaigns.</p>
      ) : (
        <>
          {showForm && (
            <Panel title="New campaign">
              <form className="grid gap-3 sm:grid-cols-2" onSubmit={create}>
                <input className={`${inputClass} sm:col-span-2`} placeholder="Campaign name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required aria-label="Campaign name" />
                <select className={inputClass} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} aria-label="Channel">
                  {CAMPAIGN_CHANNELS.map((c) => <option key={c} value={c}>{labelize(c)}</option>)}
                </select>
                <input className={inputClass} placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                <select className={inputClass} value={form.audience_type} onChange={(e) => setForm({ ...form, audience_type: e.target.value })} aria-label="Audience type">
                  <option value="">All contact types</option>
                  {CRM_TYPES.map((t) => <option key={t} value={t}>{labelize(t)}</option>)}
                </select>
                <select className={inputClass} value={form.audience_status} onChange={(e) => setForm({ ...form, audience_status: e.target.value })} aria-label="Audience status">
                  <option value="">Any stage</option>
                  {CRM_STATUSES.map((s) => <option key={s} value={s}>{labelize(s)}</option>)}
                </select>
                <textarea className={`${inputClass} sm:col-span-2`} rows={4} placeholder="Message…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                <p className="font-mono text-[11px] text-muted sm:col-span-2" data-testid="audience-size">
                  Audience: {audience === null ? "…" : `${audience} contact${audience === 1 ? "" : "s"}`}
                </p>
                <Button type="submit" disabled={busy === "new" || !form.name.trim()} className="sm:col-span-2">Create draft</Button>
              </form>
            </Panel>
          )}

          {loading ? (
            <p className="font-mono text-sm text-muted">Loading…</p>
          ) : campaigns.length === 0 ? (
            <p className="font-mono text-sm text-muted">No campaigns yet. Create your first one.</p>
          ) : (
            <ul className="grid gap-3">
              {campaigns.map((c) => (
                <li key={c.id} className="grid gap-2 rounded-card border border-line bg-surface p-4">
                  <div className="flex items-center justify-between gap-3">
                    <button type="button" className="text-left font-display text-lg font-black tracking-tight hover:text-accent-strong" onClick={() => openCampaign(c.id)}>{c.name}</button>
                    <StatusLabel status={c.status} />
                  </div>
                  <p className="font-mono text-[11px] text-muted">{labelize(c.channel)} · {segmentLabel(c)}{c.status === "sent" ? ` · ${c.recipient_count} sent` : ""}</p>

                  {c.status === "draft" && (
                    <div>
                      <Button variant="ghost" disabled={busy === c.id} onClick={() => send(c)}>Send</Button>
                    </div>
                  )}

                  {open === c.id && detail && (
                    <dl className="grid grid-cols-4 gap-2 border-t border-line pt-3 text-center font-mono text-xs">
                      {(["sent", "open", "click", "convert"] as const).map((k) => (
                        <div key={k}>
                          <dt className="uppercase tracking-widest text-muted">{k}</dt>
                          <dd className="text-lg font-black">{detail.stats[k]}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
